import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BaseService } from '@fluxonos/features/shared/services/base-service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@fluxonos/environments/environment';
import { PermissionsUserModel } from '@fluxonos/features/user/models/permissions-user.model';
import { PermissionsManufacturerModel } from '@fluxonos/features/user/models/permissions-manufacturer.model';
import { PermissionsCustomerModel } from '@fluxonos/features/user/models/permissions-customer.model';
import { PermissionsGridOperatorModel } from '@fluxonos/features/user/models/permissions-grid-operator.model';
import { PermissionsEnergySupplierModel } from '@fluxonos/features/user/models/permissions-energy-supplier.model';
import { PermissionsOperatorDeviceModel } from '@fluxonos/features/user/models/permissions-operator-device.model';

@Injectable({
  providedIn: 'root'
})
export class PermissionService extends BaseService {
  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _apiBase = environment.features.userModule.user.permissions;

  private readonly _permissions = signal<string[]>([]);
  private readonly _permissionsUser = signal<PermissionsUserModel | null>(null);
  private readonly _permissionsManufacturer = signal<PermissionsManufacturerModel | null>(null);
  private readonly _permissionsGridOperator = signal<PermissionsGridOperatorModel | null>(null);
  private readonly _permissionsEnergySupplier = signal<PermissionsEnergySupplierModel | null>(null);
  private readonly _permissionsCustomerModel = signal<PermissionsCustomerModel | null>(null);
  private readonly _permissionsOperatorDevice = signal<PermissionsOperatorDeviceModel | null>(null);

  /** platform admin = admin of at least one module; everyone else is a plain plant customer */
  public readonly isAnyAdmin = computed(
    () =>
      !!this._permissionsUser()?.isAdmin ||
      !!this._permissionsManufacturer()?.isAdmin ||
      !!this._permissionsCustomerModel()?.isAdmin ||
      !!this._permissionsGridOperator()?.isAdmin ||
      !!this._permissionsEnergySupplier()?.isAdmin
  );

  /** false until the permission list arrived (sign-in / app start) */
  public readonly loaded = computed(() => this._permissionsUser() !== null);

  public list(): Observable<string[]> {
    const endpoint = this.getEndpoint(this._apiBase.list);

    return this._httpClient
      .request<string[]>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        }
      })
      .pipe(
        tap((permissions) => {
          this._permissions.set(permissions);
          this._permissionsUser.set(new PermissionsUserModel(permissions));
          this._permissionsManufacturer.set(new PermissionsManufacturerModel(permissions));
          this._permissionsGridOperator.set(new PermissionsGridOperatorModel(permissions));
          this._permissionsEnergySupplier.set(new PermissionsEnergySupplierModel(permissions));
          this._permissionsCustomerModel.set(new PermissionsCustomerModel(permissions));
          this._permissionsOperatorDevice.set(new PermissionsOperatorDeviceModel(permissions));
        })
      );
  }

  public cleanUp(): void {
    this._permissions.set([]);
  }

  public get permissionsUser(): Signal<PermissionsUserModel | null> {
    return this._permissionsUser.asReadonly();
  }

  public get permissionsManufacturer(): Signal<PermissionsManufacturerModel | null> {
    return this._permissionsManufacturer.asReadonly();
  }

  public get permissionsGridOperator(): Signal<PermissionsManufacturerModel | null> {
    return this._permissionsGridOperator.asReadonly();
  }

  public get permissionsEnergySupplier(): Signal<PermissionsEnergySupplierModel | null> {
    return this._permissionsEnergySupplier.asReadonly();
  }

  public get permissionsCustomer(): Signal<PermissionsCustomerModel | null> {
    return this._permissionsCustomerModel.asReadonly();
  }

  public get permissionsOperatorDevice(): Signal<PermissionsOperatorDeviceModel | null> {
    return this._permissionsOperatorDevice.asReadonly();
  }
}
